import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { ThemePreference } from "@mtform/shared";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";

// =============================================================================
// ThemeProvider
//
// Owns the UI theme and keeps three sources of truth in sync:
//
//   1. The `data-theme` attribute on <html> (the CSS variables key
//      off this — see `apps/web/src/index.css`).
//   2. localStorage `rt_sinai_theme_v1` (cache; also read by the
//      pre-paint inline script in `index.html` so the first paint
//      never flashes the wrong theme).
//   3. The user's `themePreference` column on the server, surfaced
//      via /api/auth/me and updated via POST /api/account/theme.
//
// Three preferences are exposed to the UI:
//
//   "light"  — explicit
//   "dark"   — explicit (the legacy look)
//   "system" — follow `prefers-color-scheme`, re-resolve live when
//              the OS flips (e.g. macOS auto-darkens at sunset)
//
// A fourth value, "unset", only exists as a *transient* signal from
// the server: it means the user has never picked. The provider
// surfaces it as `needsFirstTimePick = true` so the FirstTimeThemeDialog
// can pop on bootstrap. Saving any concrete value clears it.
//
// "system" needs a media-query listener — the OS-level setting can
// change while the app is open. We register it once when the
// resolved preference is "system" and tear it down when it isn't.
// =============================================================================

const STORAGE_KEY = "rt_sinai_theme_v1";

export type Theme = "light" | "dark";
export type ResolvedThemePreference = Exclude<ThemePreference, "unset">;

interface ThemeContextValue {
  /** What the user has chosen — "light", "dark", or "system". Once
   *  they've picked, this is never "unset" again. */
  preference: ResolvedThemePreference;
  /** What the document is actually rendering — always concrete
   *  "light" or "dark", with "system" already resolved against
   *  prefers-color-scheme. Components rarely need this; use it
   *  only when a render decision depends on the active theme
   *  (e.g. a Triplink branding swap that differs between modes). */
  resolved: Theme;
  /** True when the server returned `"unset"` for this user — the
   *  caller renders the first-time picker once and calls
   *  `setPreference` to clear it. */
  needsFirstTimePick: boolean;
  /** Save the user's pick. Writes to localStorage + the DOM
   *  immediately (so the UI flips with zero perceived latency),
   *  then fires the POST /api/account/theme write in the background
   *  so the choice follows the user across devices. */
  setPreference: (next: ResolvedThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [preference, setPreferenceState] = useState<ResolvedThemePreference>(
    () => readCachedPreference(),
  );
  const [needsFirstTimePick, setNeedsFirstTimePick] = useState(false);

  // Track whether we've reconciled with the server yet for this
  // session. Without this we'd flip the local cache to whatever the
  // server says before the user has had a chance to confirm — which
  // is the wrong order for the "unset" case (the dialog has to fire
  // *before* we overwrite anything).
  const reconciledForUserIdRef = useRef<string | null>(null);

  // Reconcile with the server every time the auth state lands on a
  // new authenticated user. The pre-paint script already set
  // data-theme from localStorage; this step adopts the server's
  // value when it differs.
  useEffect(() => {
    if (auth.state.status !== "authenticated") {
      // Keep using whatever we resolved from localStorage; we just
      // can't sync to the server yet.
      reconciledForUserIdRef.current = null;
      return;
    }
    const serverPref = auth.state.user.themePreference;
    const userId = auth.state.user.id;
    if (reconciledForUserIdRef.current === userId) return;
    reconciledForUserIdRef.current = userId;

    if (serverPref === "unset") {
      // Server has nothing on file → trigger the picker. Don't touch
      // the local cache yet; if the user reloads before picking, the
      // dialog opens again next time too.
      setNeedsFirstTimePick(true);
      return;
    }
    // Server has a value → make it the canonical preference. Always
    // overwrite the local cache so a stale localStorage from a
    // different machine doesn't override the server.
    setNeedsFirstTimePick(false);
    if (serverPref !== preference) {
      setPreferenceState(serverPref);
      writeCachedPreference(serverPref);
    } else {
      // Same value; just make sure localStorage agrees (might have
      // been cleared by the user).
      writeCachedPreference(serverPref);
    }
    // We deliberately exclude `preference` from deps — only the
    // *server* changing should trigger a server-driven sync. Local
    // changes are handled in setPreference below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.state]);

  // Apply data-theme on every preference change. Resolve "system"
  // against prefers-color-scheme here; the media-query listener
  // below keeps it live.
  useEffect(() => {
    applyResolvedTheme(preference);
  }, [preference]);

  // Live OS-level theme listener — only active when the user picked
  // "system". Tearing down on explicit picks keeps the no-op fast
  // path clean.
  useEffect(() => {
    if (preference !== "system") return;
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-color-scheme: light)");
    const handler = () => applyResolvedTheme("system");
    // Both legacy + modern listener signatures; older Safari needs
    // addListener, current browsers want addEventListener("change").
    if (mql.addEventListener) {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, [preference]);

  const setPreference = useCallback(
    (next: ResolvedThemePreference) => {
      setPreferenceState(next);
      writeCachedPreference(next);
      setNeedsFirstTimePick(false);
      // Server sync. Fire-and-forget — the local UI already updated,
      // and a transient failure shouldn't block the user. We log so
      // a SendGrid-style "this never reaches the DB" pattern is
      // visible in Cloud Logging if it ever happens.
      if (auth.state.status === "authenticated") {
        void api.account.updateTheme({ theme: next }).catch((err) => {
          // 401 is normal during logout races; suppress those.
          if (err instanceof ApiError && err.code === "UNAUTHENTICATED") return;
          console.warn("[theme] /api/account/theme write failed:", err);
        });
      }
    },
    [auth.state.status],
  );

  const resolved = useMemo<Theme>(() => resolveTheme(preference), [preference]);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, needsFirstTimePick, setPreference }),
    [preference, resolved, needsFirstTimePick, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used inside <ThemeProvider />");
  }
  return ctx;
}

// =============================================================================
// Internals
// =============================================================================

function readCachedPreference(): ResolvedThemePreference {
  if (typeof window === "undefined") return "dark";
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === "light" || raw === "dark" || raw === "system") return raw;
  } catch {
    /* localStorage can throw in private windows / disabled storage */
  }
  // No cached value — default to "dark" to match the legacy look the
  // 100+ active users are already on. The first-time picker will run
  // once the auth state arrives and tells us the server hasn't seen
  // a pick for this user yet.
  return "dark";
}

function writeCachedPreference(value: ResolvedThemePreference): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* same private-window caveat */
  }
}

function resolveTheme(pref: ResolvedThemePreference): Theme {
  if (pref === "system") {
    if (typeof window === "undefined" || !window.matchMedia) return "dark";
    return window.matchMedia("(prefers-color-scheme: light)").matches
      ? "light"
      : "dark";
  }
  return pref;
}

function applyResolvedTheme(pref: ResolvedThemePreference): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", resolveTheme(pref));
}
